"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Modal = exports.modalFocusLock = void 0;
const tslib_1 = require("tslib");
const jsx_runtime_1 = require("react/jsx-runtime");
const React = tslib_1.__importStar(require("react"));
const classnames_1 = tslib_1.__importDefault(require("classnames"));
const react_dom_1 = require("react-dom");
const react_focus_lock_1 = tslib_1.__importDefault(require("react-focus-lock"));
const hooks_1 = require("@nbc-design/hooks");
const theme_provider_1 = require("@nbc-design/theme-provider");
const tokens_json_1 = tslib_1.__importDefault(require("@nbc-design/tokens/dist/styles/tokens.json"));
const body_1 = require("./body");
const closeBtn_1 = require("./closeBtn");
const footer_1 = require("./footer");
const header_1 = require("./header");
const modalContext_1 = require("./modalContext");
require("./modal.css");
exports.modalFocusLock = { group: 'dsc-focusLock' };
/** A modal is a dialog box/popup window that is displayed on top of the current page */
exports.Modal = React.forwardRef(({ appearance = 'dark', size = 'medium', children, className, onModalClose, preventCloseClick, titleId, dataTest = 'modal', triggerClosing = false, focusLockProps = exports.modalFocusLock, createPortalElement = document.body, isLoading = false, loader, defaultLoaderAriaLabel, resizable = false, }, forwardRef) => {
    const [isFadeOut, setIsFadeOut] = React.useState(false);
    const [isFooterSticky, setIsFooterSticky] = React.useState(false);
    const [hasFocusedDatePicker, setHasFocusedDatePicker] = React.useState(false);
    const [lastFocus, setLastFocus] = React.useState(null);
    const [resizableSize, setResizableSize] = React.useState(size);
    const [allowBackgroundClick, setAllowBackgroundClick] = React.useState(false);
    const modal = React.useRef(null);
    const closeButtonRef = React.useRef(null);
    const modalClass = 'is-modalOpen';
    const themeContext = (0, theme_provider_1.useThemeContext)() ?? { theme: 'legacy', colorScheme: 'light' };
    React.useEffect(() => {
        setResizableSize(size);
    }, [size]);
    const handleFadeOutCloseAndFocus = React.useCallback((onClick) => {
        const scrollPageValue = parseInt(document.body.getAttribute('scrollPage') || '0', 10);
        setIsFadeOut(true);
        setTimeout(() => {
            onModalClose?.();
            onClick?.();
            document.body.style.paddingRight =
                document.body.getAttribute('inititalPaddingRight') || '0px';
            document.body.classList.remove(modalClass);
            window.scrollTo(0, scrollPageValue);
            document.body.removeAttribute('inititalPaddingRight');
            document.body.removeAttribute('scrollPage');
            setTimeout(() => {
                lastFocus?.focus?.();
            }, 1);
        }, 320);
    }, [lastFocus, onModalClose]);
    React.useEffect(() => {
        const syncHeight = () => {
            document.documentElement.style.setProperty('--window-inner-height', `${window.innerHeight}px`);
        };
        if (document.activeElement instanceof HTMLInputElement ||
            document.activeElement instanceof HTMLButtonElement) {
            if (closeButtonRef.current !== document.activeElement) {
                setLastFocus(document.activeElement);
            }
        }
        const initialPaddingRight = document.body.style.paddingRight;
        const scrollPage = window.pageYOffset || 0;
        if (document?.body?.getAttribute('inititalPaddingRight') === null) {
            const documentWidth = document.documentElement.clientWidth;
            const windowWidth = window.innerWidth;
            const scrollBarWidth = windowWidth - documentWidth;
            document.body.style.paddingRight = `${scrollBarWidth}px`;
            document.body.style.top = `-${window.scrollY}px`;
            document.body.classList.add(modalClass);
            document.documentElement.classList.add(modalClass);
            document.body.setAttribute('inititalPaddingRight', initialPaddingRight || '0px');
            document.body.setAttribute('scrollPage', scrollPage.toString());
            syncHeight();
        }
        window.addEventListener('resize', syncHeight);
        return () => {
            document.body.classList.remove(modalClass);
            document.documentElement.classList.remove(modalClass);
            window.removeEventListener('resize', syncHeight);
        };
    }, []);
    // Used to make page scrollable when modal is resizable & xSmall
    React.useEffect(() => {
        const modalExists = document.querySelector('.dsc-modal.resizable.is-xSmall');
        if (modalExists) {
            document.body.classList.add('is-xSmall-modal');
        }
        else {
            document.body.classList.remove('is-xSmall-modal');
        }
        return () => {
            document.body.classList.remove('is-xSmall-modal');
        };
    }, [resizable, resizableSize]);
    React.useEffect(() => {
        const el = modal.current;
        if (!el)
            return;
        const isDraggingMode = resizable && resizableSize === 'xSmall';
        if (!isDraggingMode) {
            el.style.left = '';
            el.style.top = '';
        }
    }, [resizable, resizableSize]);
    const effectiveSize = resizable ? resizableSize : size;
    const classes = (0, classnames_1.default)('dsc-modal', appearance === 'light' ? `is-${appearance}` : null, effectiveSize !== 'medium' ? `is-${effectiveSize}` : null, isFadeOut && 'is-fadeOut', resizable && 'resizable', className);
    const handleCloseClick = React.useCallback((onClick) => {
        preventCloseClick ? preventCloseClick() : handleFadeOutCloseAndFocus(onClick);
    }, [handleFadeOutCloseAndFocus, preventCloseClick]);
    // Close modal on escape
    const handleUserKeyPress = React.useCallback((event) => {
        const { key } = event;
        if (key === 'Escape' &&
            !document.activeElement?.closest('.dsc-tooltip') &&
            !document.activeElement?.closest('.dsc-popover') &&
            !hasFocusedDatePicker) {
            handleCloseClick();
        }
        else if (key === 'Escape' && hasFocusedDatePicker) {
            setHasFocusedDatePicker(false);
        }
    }, [handleCloseClick, hasFocusedDatePicker]);
    const handleBackgroundClick = React.useCallback((e) => {
        if (!allowBackgroundClick && modal?.current?.children[0].children[0] === e.target) {
            handleCloseClick();
        }
    }, [allowBackgroundClick, handleCloseClick]);
    const determineIfFocusIsOnDatePicker = React.useCallback(() => {
        if (document.activeElement
            ?.closest('.dsc-datePicker')
            ?.querySelector('.react-datepicker__tab-loop')) {
            setHasFocusedDatePicker(true);
        }
    }, []);
    const resetIfFocusIsOnDatePicker = (event) => {
        const target = event?.target;
        if (!target.closest('.dsc-datePicker')) {
            setHasFocusedDatePicker(false);
        }
    };
    React.useEffect(() => {
        if (onModalClose) {
            window.addEventListener('keydown', handleUserKeyPress);
            document.addEventListener('mousedown', handleBackgroundClick);
            window.addEventListener('focusin', determineIfFocusIsOnDatePicker);
            window.addEventListener('focusout', resetIfFocusIsOnDatePicker);
        }
        return () => {
            if (onModalClose) {
                window.removeEventListener('keydown', handleUserKeyPress);
                document.removeEventListener('mousedown', handleBackgroundClick);
                window.removeEventListener('focusin', determineIfFocusIsOnDatePicker);
                window.removeEventListener('focusout', determineIfFocusIsOnDatePicker);
            }
        };
    }, [determineIfFocusIsOnDatePicker, handleBackgroundClick, handleUserKeyPress, onModalClose]);
    React.useEffect(() => {
        triggerClosing && handleFadeOutCloseAndFocus();
    }, [handleFadeOutCloseAndFocus, triggerClosing]);
    React.useEffect(() => {
        if (isLoading && modal.current) {
            modal.current.focus();
        }
    }, [isLoading]);
    const value = React.useMemo(() => ({
        handleCloseClick,
        titleId,
        setIsFooterSticky,
        isFooterSticky,
        closeButtonRef,
        isLoading,
        loader,
        currentSize: effectiveSize,
        defaultLoaderAriaLabel,
        setResizableSize,
        setAllowBackgroundClick,
        resizable,
        resizableSize,
    }), [
        handleCloseClick,
        titleId,
        setIsFooterSticky,
        isFooterSticky,
        closeButtonRef,
        isLoading,
        loader,
        effectiveSize,
        defaultLoaderAriaLabel,
        setResizableSize,
        setAllowBackgroundClick,
        resizable,
        resizableSize,
    ]);
    const handleMouseDown = (e) => {
        if (!resizable || resizableSize !== 'xSmall')
            return;
        const modalElement = modal.current;
        if (!modalElement)
            return;
        const startX = e.clientX;
        const startY = e.clientY;
        const startLeft = modalElement.offsetLeft;
        const startTop = modalElement.offsetTop;
        const handleMouseMove = (moveEvent) => {
            const newLeft = startLeft + moveEvent.clientX - startX;
            const newTop = startTop + moveEvent.clientY - startY;
            modalElement.style.left = `${newLeft}px`;
            modalElement.style.top = `${newTop}px`;
        };
        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };
    const isDesktop = (0, hooks_1.useMediaQuery)(`(min-width: ${tokens_json_1.default['bp-large']}px)`);
    return (0, react_dom_1.createPortal)((0, jsx_runtime_1.jsx)(react_focus_lock_1.default, { ...focusLockProps, ref: forwardRef, children: (0, jsx_runtime_1.jsx)("div", { ref: modal, tabIndex: 0, className: classes, role: "dialog", "aria-modal": "true", "aria-labelledby": titleId && titleId, "data-test": dataTest, ...(themeContext && {
                'data-theme': themeContext.theme,
                'data-color-scheme': themeContext.colorScheme,
            }), ...(resizable &&
                isDesktop &&
                resizableSize === 'xSmall' && {
                onMouseDown: handleMouseDown,
            }), children: (0, jsx_runtime_1.jsx)("div", { className: "dsc-modal__inner", role: "document", children: (0, jsx_runtime_1.jsx)("div", { className: "dsc-modal__closeZone", children: (0, jsx_runtime_1.jsx)("div", { className: (0, classnames_1.default)('dsc-modal__container', isFooterSticky && 'is-footer-sticky'), children: (0, jsx_runtime_1.jsx)(modalContext_1.ModalContext.Provider, { value: value, children: children }) }) }) }) }) }), createPortalElement);
});
exports.Modal.Header = header_1.Header;
exports.Modal.Body = body_1.Body;
exports.Modal.Footer = footer_1.Footer;
exports.Modal.CloseBtn = closeBtn_1.CloseBtn;
exports.Modal.displayName = 'Modal';
exports.default = exports.Modal;
//# sourceMappingURL=modal.js.map